import { useState, useCallback, useMemo, useRef } from "react";

/**
 * Hook compartido para gestionar filtros de los tabs de Emails.
 *
 * IMPORTANTE: el objeto retornado se memoriza para que sea estable entre
 * renders mientras el `state` no cambie. Esto previene loops infinitos en
 * `useEffect([filters])` y `useCallback([..., filters])` que de otro modo
 * dispararían re-renders en cascada (cada llamada al hook creaba un objeto
 * nuevo → useCallback se invalidaba → useEffect corría → setState → loop).
 *
 * También se memoriza `defaults` mediante un useRef para que pasarlo inline
 * (como `useEmailFilters({...})`) no rompa la estabilidad.
 *
 * Uso:
 *   const filters = useEmailFilters({ q: "", status: "", date_from: "", date_to: "" });
 *   filters.set("q", "hola");
 *   const qp = filters.toQueryParams(); // URLSearchParams
 *   filters.reset();
 */
export function useEmailFilters(defaults) {
  // Snapshot estable de defaults (capturado en el primer render).
  // Si el caller lo pasa inline en cada render, igual usamos el primero.
  const defaultsRef = useRef(defaults);

  const [state, setState] = useState(defaultsRef.current);

  const set = useCallback((key, value) => {
    setState((s) => ({ ...s, [key]: value }));
  }, []);

  const setMany = useCallback((updates) => {
    setState((s) => ({ ...s, ...updates }));
  }, []);

  const reset = useCallback(() => setState(defaultsRef.current), []);

  const toQueryParams = useCallback(() => {
    const qp = new URLSearchParams();
    Object.entries(state).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;
      if (typeof v === "boolean") {
        qp.set(k, v ? "true" : "false");
      } else {
        qp.set(k, String(v));
      }
    });
    return qp;
  }, [state]);

  const activeCount = useMemo(() => {
    const def = defaultsRef.current;
    return Object.entries(state).filter(([k, v]) => {
      return v !== def[k] && v !== "" && v !== null && v !== undefined;
    }).length;
  }, [state]);

  // Memoizamos el objeto retornado para que sea estable mientras state no cambie.
  // Las funciones (set, setMany, reset) son estables por sí solas; toQueryParams
  // y activeCount sí dependen de state.
  return useMemo(
    () => ({ state, set, setMany, reset, toQueryParams, activeCount }),
    [state, set, setMany, reset, toQueryParams, activeCount]
  );
}
